"use client";

import type { ChangeEvent } from "react";
import type { MotionValue } from "motion/react";
import type { Segment, TranscriptionResponse } from "@ui/components/demos/STT/types";

import { useTranslations } from "next-intl";

import { Skeleton } from "@ui/components/core/Skeleton";
import { LimitReachedOverlay } from "@ui/components/demos/LimitReachedOverlay";
import { STTHeroTranscript } from "./STTHeroTranscript";
import { appUrls } from "@/constants";
import { STTUploadingView } from "./STTUploadingView";
import { DottedBorder } from "@ui/components/core/GradientBorder";
import type { Media } from "@mw/shared/payload-types";

import type { STTHeroDisplayMode } from "./hooks/useSTTHeroPlayback";
import { STTFloatingCardHeader } from "./STTFloatingCardHeader";
import { useSTTFloatingCardHeader } from "./hooks/useSTTFloatingCardHeader";
import { STTFloatingCardFooter } from "./STTFloatingCardFooter";

export interface STTFloatingCardProps {
  displayMode?: STTHeroDisplayMode;
  sampleThumbnail?: Media | null;
  uploadedMediaKind?: "audio" | "video" | null;
  sampleName: string;
  segments: Segment[];
  isLoading: boolean;
  isPlaying: boolean;
  isUploading?: boolean;
  uploadFileName?: string | null;
  uploadedTranscription?: TranscriptionResponse | null;
  uploadedThumbnailUrl?: string | null;
  currentTime: MotionValue<number>;
  onTogglePlay: () => void;
  onWordClick: (time: number) => void;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onUrlSubmit: (url: string) => void | Promise<void>;
  onDownload?: () => void;
  onTranscriptionDownloaded?: (download_format: string) => void;
  showLimitReachedOverlay?: boolean;
  allowUrlTranscription?: boolean;
}

export function STTFloatingCard({
  displayMode = "default",
  sampleThumbnail = null,
  uploadedMediaKind = null,
  sampleName,
  segments,
  isLoading,
  isPlaying,
  isUploading = false,
  uploadFileName = null,
  uploadedTranscription = null,
  uploadedThumbnailUrl = null,
  currentTime,
  onTogglePlay,
  onWordClick,
  onFileChange,
  onUrlSubmit,
  onDownload,
  onTranscriptionDownloaded,
  showLimitReachedOverlay = false,
  allowUrlTranscription = false,
}: STTFloatingCardProps) {
  const tSpeechToText = useTranslations("ClientKeys.SpeechToText");

  const headerModel = useSTTFloatingCardHeader({
    displayMode,
    sampleThumbnail,
    uploadedMediaKind,
    sampleName,
    segments,
    uploadFileName,
    uploadedThumbnailUrl,
  });

  if (isUploading) {
    return (
      <div
        className='tw-mx-auto tw-w-[375px] tw-max-w-full tw-h-[min(530px,calc(100svh_-_210px))] tw-min-h-0 tw-bg-white tw-rounded-3xl tw-flex tw-flex-col tw-gap-6 tw-p-6 tw-overflow-hidden tw-relative'
        style={{ boxShadow: "0 4px 24px rgb(0 0 0 / 0.12), 0 1px 2px rgb(0 0 0 / 0.06)" }}
      >
        <STTUploadingView uploadFileName={uploadFileName} />
      </div>
    );
  }

  return (
    <div
      className='tw-mx-auto tw-w-[375px] tw-max-w-full tw-h-[min(530px,calc(100svh_-_210px))] tw-min-h-0 tw-bg-white tw-rounded-3xl tw-flex tw-flex-col tw-gap-6 tw-p-6 tw-overflow-hidden tw-relative'
      style={{ boxShadow: "0 4px 24px rgb(0 0 0 / 0.12), 0 1px 2px rgb(0 0 0 / 0.06)" }}
    >
      {showLimitReachedOverlay && (
        <div className='tw-absolute tw-inset-0 tw-z-30 tw-flex tw-items-center tw-justify-center tw-rounded-3xl tw-bg-cream'>
          <LimitReachedOverlay
            rounded
            variant='redesigned'
            ctaLink={appUrls.speechToText}
            description={tSpeechToText("widget.quota_exceeded_description")}
            title={tSpeechToText("widget.quota_exceeded_title")}
          />
        </div>
      )}
      <STTFloatingCardHeader model={headerModel} />

      {headerModel.showDottedDivider ? (
        <div className='tw-relative tw-h-0.5 tw-shrink-0'>
          <DottedBorder side='top' color='rgba(0,0,0,0.1)' />
        </div>
      ) : null}

      <div className='tw-flex-1 tw-min-h-0 tw-relative tw-overflow-hidden tw-flex tw-flex-col'>
        {isLoading ? (
          <div className='tw-flex tw-flex-col tw-gap-3 tw-opacity-40'>
            <Skeleton className='tw-h-4 tw-w-3/4' />
            <Skeleton className='tw-h-4 tw-w-full' />
            <Skeleton className='tw-h-4 tw-w-full' />
            <Skeleton className='tw-h-4 tw-w-5/6' />
            <Skeleton className='tw-h-4 tw-w-full' />
            <Skeleton className='tw-h-4 tw-w-full' />
            <Skeleton className='tw-h-4 tw-w-4/5' />
          </div>
        ) : (
          <STTHeroTranscript
            currentTime={currentTime}
            segments={segments}
            onWordClick={onWordClick}
            isPlaying={isPlaying}
            bottomOffset={98}
          />
        )}
      </div>

      <STTFloatingCardFooter
        isPlaying={isPlaying}
        uploadedTranscription={uploadedTranscription}
        uploadFileName={uploadFileName}
        sampleName={sampleName}
        allowUrlTranscription={allowUrlTranscription}
        onTogglePlay={onTogglePlay}
        onFileChange={onFileChange}
        onUrlSubmit={onUrlSubmit}
        onDownload={onDownload}
        onTranscriptionDownloaded={onTranscriptionDownloaded}
      />
    </div>
  );
}

export default STTFloatingCard;
